import { useDialogRoute } from "@dash/dialog";
import { Button } from "@dash/form";
import { SelectDialog } from "@dash/select";
import { TileCard } from "@dash/tile";
import { useToast } from "@dash/toast";
import { IconType } from "react-icons/lib";
import {
  MdApartment,
  MdAttachMoney,
  MdDeleteOutline,
  MdOutlineAdd,
  MdOutlinePersonAdd,
  MdOutlineWarehouse,
  MdStorefront,
} from "react-icons/md";
import { useQueryClient } from "react-query";

import { Relation, relationConstraints, RelationType } from "@megaron/crm-contracts";
import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { newUuid, Uuid } from "@megaron/uuid";
import { getMegaraxPath } from "@megaron/megarax-uri";

type Props = {
  value?: { name: string; relatedCustomerUuid: Uuid; relationUuid: Uuid };
  relationType: RelationType;
  queryKey: string | string[];
  customerUuid: Uuid;
  existingRelation?: Relation[];
};

export const RelationTile: React.FC<Props> = ({ value, relationType, customerUuid, queryKey, existingRelation }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const docs = useServiceClient("docs");

  const category = relationConstraints[relationType].parentCategory;

  const relationInfo: Record<RelationType, { Icon: IconType; label: string }> = {
    distributor: { Icon: MdOutlineWarehouse, label: "Dystrybutor" },
    chain: { Icon: MdApartment, label: "Sieć" },
    payer: { Icon: MdAttachMoney, label: "Płatnik" },
    supplyPoint: { Icon: MdStorefront, label: "Punkt sprzedaży" },
    invitedBy: { Icon: MdOutlinePersonAdd, label: "Zaproszony przez" },
  };

  const addRelationMutation = useClientManager("crm").addCustomerRelation().useMutation();
  const removeRelationMutation = useClientManager("crm").removeCustomerRelation().useMutation();

  const existingRelationId = existingRelation?.map((e) => e.parentId);

  const addRelation = (parentId: Uuid, onClose: () => void) => {
    if (parentId !== customerUuid) {
      addRelationMutation.mutate(
        {
          uuid: newUuid(),
          parentId: parentId,
          childId: customerUuid,
          type: relationType,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
            onClose();
          },
          onError: () => toast.error(`Nie udało się dodać relacji`),
        },
      );
    } else {
      toast.error("Nie możesz dodać samego siebie jako relacji.");
    }
  };

  const removeRelation = (relationUuid: Uuid) => {
    removeRelationMutation.mutate(
      {
        relationUuid: relationUuid,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKey);
        },
        onError: () => toast.error(`Nie udało się usunąć relacji`),
      },
    );
  };

  const dialog = useDialogRoute(`/add-relation/${relationType}`, ({ onClose }) => (
    <SelectDialog
      label="Dodaj relacje"
      search={async (searchText) => {
        const results = await docs.searchCustomers({
          categories: [category],
          limit: 100,
          searchText: searchText,
        });

        return results
          ? (results.value?.items.map((result) => ({ label: result.name, value: result.uuid })) ?? [])
          : [];
      }}
      initiallySelectedValues={existingRelationId}
      variant="single-select"
      onSelect={(option) => option?.value && addRelation(option.value, onClose)}
      isOpen
      isUnselectDisabled
      onClose={onClose}
      noTrigger
    />
  ));

  const Icon = relationInfo[relationType].Icon;

  return (
    <>
      <TileCard
        href={value ? getMegaraxPath.uri("megarax://crm/customers/id/{id}", { id: value.relatedCustomerUuid }) : undefined}
        css={{
          "&:hover": {
            cursor: value?.relatedCustomerUuid ? "pointer" : "default",
          },
        }}
        isActive={!!value}
        actions={
          relationType !== "invitedBy" ? (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                value ? removeRelation(value.relationUuid) : dialog.open();
              }}
              icon={value ? <MdDeleteOutline size={16} /> : <MdOutlineAdd size={16} />}
              variant="outline"
              css={{ border: "none", padding: "0.5rem" }}
            />
          ) : undefined
        }
        label={relationInfo[relationType].label}
        value={value?.name || "Dodaj"}
        Icon={Icon}
      />
      {!value && dialog.element}
    </>
  );
};
